/**
 * My Identity — the consumer's Personal Data Vault entry.
 * BRD §7.1, §10 — consumer enters identity ONCE, KYC vendor verifies,
 * institutions receive only what the consumer consents to.
 */
import { useEffect, useState } from "react";
import { WalletShell, NAVY, GOLD } from "@/components/wallet/WalletShell";
import {
  UserSquare2, Loader2, ShieldCheck, AlertTriangle, CheckCircle2,
  Clock, Save, Fingerprint, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import {
  getIdentity, saveIdentity, verifyKyc, eraseIdentity,
  SA_PROVINCES, ID_DOC_TYPES, KYC_STATUS_LABELS,
} from "@/lib/personal-data-vault";
import { emitSignal } from "@/lib/wallet-signal";

const EMPTY = {
  first_name: "", last_name: "", date_of_birth: "",
  national_id_number: "", id_document_type: "sa_id",
  email: "", phone: "",
  address_line1: "", address_line2: "", city: "",
  postal_code: "", province: "gauteng", country: "ZA",
};

export default function WalletIdentity() {
  const [entry, setEntry] = useState(null);      // server-known entry
  const [form, setForm] = useState(EMPTY);       // form state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    emitSignal("wallet.identity.opened");
    getIdentity()
      .then((e) => {
        setEntry(e);
        if (e) setForm({
          first_name: e.first_name, last_name: e.last_name,
          date_of_birth: e.date_of_birth,
          national_id_number: e.national_id_number,
          id_document_type: e.id_document_type,
          email: e.email || "", phone: e.phone || "",
          address_line1: e.address_line1, address_line2: e.address_line2 || "",
          city: e.city, postal_code: e.postal_code, province: e.province,
          country: e.country,
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const cleanEmail = form.email && form.email.trim() ? form.email.trim() : null;
      const cleanPhone = form.phone && form.phone.trim() ? form.phone.trim() : null;
      const saved = await saveIdentity({ ...form, email: cleanEmail, phone: cleanPhone });
      setEntry(saved);
      emitSignal("wallet.identity.saved");
      toast.success("Identity saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onVerify = async () => {
    setVerifying(true);
    try {
      const updated = await verifyKyc();
      setEntry(updated);
      emitSignal(`wallet.kyc.${updated.kyc_status}`, { provider: updated.kyc_provider });
      if (updated.kyc_status === "verified") toast.success("KYC verified");
      else if (updated.kyc_status === "in_review") toast.info("Under manual review");
      else toast.error(updated.kyc_failure_reason || "Verification failed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not run verification");
    } finally {
      setVerifying(false);
    }
  };

  const onErase = async () => {
    if (!window.confirm("This deletes your identity from Altara. Institutions will lose access to any data reads not already completed. Are you sure?")) return;
    try {
      await eraseIdentity();
      emitSignal("wallet.identity.erased");
      toast.success("Identity erased");
      setEntry(null);
      setForm(EMPTY);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erase failed");
    }
  };

  if (loading) {
    return (
      <WalletShell activeTab="record">
        <div className="max-w-3xl mx-auto px-4 py-10 text-sm text-slate-500 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      </WalletShell>
    );
  }

  return (
    <WalletShell activeTab="record">
      <div className="max-w-3xl mx-auto px-4 lg:px-10 py-8 lg:py-10 space-y-6" data-testid="identity-page">
        <header>
          <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Digital Trust Wallet</div>
          <h1 className="font-display text-3xl lg:text-4xl font-semibold" style={{ color: NAVY }}>My Identity</h1>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl">
            Enter your details once. Altara verifies through independent KYC providers. Institutions receive only what you approve — never the underlying data unless you say yes.
          </p>
        </header>

        {entry && <KycBadgeRow entry={entry} onVerify={onVerify} verifying={verifying} />}

        <form onSubmit={onSave} className="space-y-6" data-testid="identity-form">
          <Section icon={UserSquare2} title="Personal details">
            <Row>
              <Field label="First name" testid="pdv-first-name" required value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} />
              <Field label="Last name" testid="pdv-last-name" required value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} />
            </Row>
            <Row>
              <Field label="Date of birth (YYYY-MM-DD)" testid="pdv-dob" placeholder="1990-06-15" required value={form.date_of_birth} onChange={(v) => setForm({ ...form, date_of_birth: v })} type="date" />
              <Select
                label="ID document"
                testid="pdv-id-type"
                value={form.id_document_type}
                onChange={(v) => setForm({ ...form, id_document_type: v })}
                options={ID_DOC_TYPES}
              />
            </Row>
            <Field label="ID number" testid="pdv-id-number" required value={form.national_id_number} onChange={(v) => setForm({ ...form, national_id_number: v })} />
          </Section>

          <Section icon={Fingerprint} title="Contact (optional but recommended)">
            <Row>
              <Field label="Email" testid="pdv-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <Field label="Phone" testid="pdv-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            </Row>
          </Section>

          <Section icon={ShieldCheck} title="Residential address">
            <Field label="Street address" testid="pdv-line1" required value={form.address_line1} onChange={(v) => setForm({ ...form, address_line1: v })} />
            <Field label="Address line 2" testid="pdv-line2" value={form.address_line2} onChange={(v) => setForm({ ...form, address_line2: v })} />
            <Row>
              <Field label="City" testid="pdv-city" required value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
              <Field label="Postal code" testid="pdv-postal" required value={form.postal_code} onChange={(v) => setForm({ ...form, postal_code: v })} />
            </Row>
            <Select
              label="Province"
              testid="pdv-province"
              value={form.province}
              onChange={(v) => setForm({ ...form, province: v })}
              options={SA_PROVINCES}
            />
          </Section>

          <div className="flex flex-wrap gap-3 pt-2">
            <button
              type="submit"
              data-testid="pdv-save-btn"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              style={{ background: NAVY }}
            >
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save identity"}
            </button>
            {entry && (
              <button
                type="button"
                onClick={onErase}
                data-testid="pdv-erase-btn"
                className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold border border-rose-200 text-rose-700 hover:bg-rose-50"
              >
                <Trash2 className="w-4 h-4" /> Erase (POPIA)
              </button>
            )}
          </div>
        </form>

        <div className="text-[11px] text-slate-500 rounded-lg bg-slate-50 border border-slate-200 p-3">
          <strong>What Altara does with this:</strong> your identity lives in your Personal Data Vault. When an institution asks for KYC, affordability, address confirmation or anything else, you&rsquo;ll see a request on <em>My Consent</em>. Altara never shares anything unless you approve.
        </div>
      </div>
    </WalletShell>
  );
}

function KycBadgeRow({ entry, onVerify, verifying }) {
  const meta = KYC_STATUS_LABELS[entry.kyc_status] || KYC_STATUS_LABELS.not_started;
  const toneClass = {
    slate:  { bg: "#F1F5F9", color: "#334155", Icon: Clock },
    emerald:{ bg: "#D1FAE5", color: "#065F46", Icon: CheckCircle2 },
    amber:  { bg: "#FEF3C7", color: "#B45309", Icon: Clock },
    rose:   { bg: "#FEE2E2", color: "#7F1D1D", Icon: AlertTriangle },
  }[meta.tone] || { bg: "#F1F5F9", color: "#334155", Icon: Clock };
  const Icon = toneClass.Icon;
  return (
    <section
      data-testid="kyc-status-card"
      className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5 flex items-center justify-between gap-4"
    >
      <div className="flex items-start gap-3 min-w-0">
        <span
          data-testid={`kyc-status-${entry.kyc_status}`}
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded font-semibold"
          style={{ background: toneClass.bg, color: toneClass.color }}
        >
          <Icon className="w-3 h-3" /> {meta.label}
        </span>
        <div className="min-w-0">
          <div className="text-sm font-semibold" style={{ color: NAVY }}>KYC verification</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {entry.kyc_status === "verified" && entry.kyc_expires_at && (
              <>Verified via <span className="font-semibold">{entry.kyc_provider}</span> · valid until {new Date(entry.kyc_expires_at).toLocaleDateString()}</>
            )}
            {entry.kyc_status === "failed" && (entry.kyc_failure_reason || "Please review your details.")}
            {entry.kyc_status === "in_review" && "A human reviewer is looking at your details."}
            {entry.kyc_status === "not_started" && "Trigger verification once your details are correct."}
          </div>
        </div>
      </div>
      <button
        onClick={onVerify}
        disabled={verifying || entry.kyc_status === "pending"}
        data-testid="kyc-verify-btn"
        className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white flex-shrink-0 disabled:opacity-50"
        style={{ background: GOLD }}
      >
        <ShieldCheck className="w-4 h-4" />
        {verifying ? "Verifying…" : entry.kyc_status === "verified" ? "Re-verify" : "Run verification"}
      </button>
    </section>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5 space-y-3">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" style={{ color: GOLD }} />
        <div className="text-sm font-semibold" style={{ color: NAVY }}>{title}</div>
      </div>
      {children}
    </section>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, testid, value, onChange, required, type = "text", placeholder }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">{label}{required && " *"}</div>
      <input
        data-testid={testid}
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A1F44]/20 focus:border-[#0A1F44]"
      />
    </label>
  );
}

function Select({ label, testid, value, onChange, options }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A1F44]/20 focus:border-[#0A1F44]"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
